import homeRuntimeArt from './home-runtime-art.json';
import {
  applyIslandHomography,
  islandHomography,
  SETTLEMENT_ISLAND_DISTRICTS,
  type IslandQuad,
} from './settlement-island-registration';

const naturalShoreGeometry = {
  width: 1536,
  height: 1024,
  // The legal rectangle is on the grassy interior. The irregular shore extends beyond it.
  buildableSourceQuad: [
    { x: 594, y: 279.25 },
    { x: 1206, y: 491.75 },
    { x: 866, y: 610.75 },
    { x: 254, y: 398.25 },
  ] as IslandQuad,
  reviewStatus: 'needs_cultural_review',
} as const;

/** Distinct shores; the 15% inset adds dry margin without changing any saved plot. */
export const settlementExpansionGroundManifest = {
  garden_quarter: {
    ...naturalShoreGeometry,
    id: 'natural_shore_garden_v1',
    url: homeRuntimeArt['natural-shore-garden-v1'].url,
    sourceUrl: homeRuntimeArt['natural-shore-garden-v1'].sourceUrl,
  },
  artisan_quarter: {
    ...naturalShoreGeometry,
    id: 'natural_shore_artisan_v1',
    url: homeRuntimeArt['natural-shore-artisan-v1'].url,
    sourceUrl: homeRuntimeArt['natural-shore-artisan-v1'].sourceUrl,
  },
  market_quarter: {
    ...naturalShoreGeometry,
    id: 'natural_shore_market_v1',
    url: homeRuntimeArt['natural-shore-market-v1'].url,
    sourceUrl: homeRuntimeArt['natural-shore-market-v1'].sourceUrl,
  },
  ceremonial_quarter: {
    ...naturalShoreGeometry,
    id: 'natural_shore_ceremonial_v1',
    url: homeRuntimeArt['natural-shore-ceremonial-v1'].url,
    sourceUrl: homeRuntimeArt['natural-shore-ceremonial-v1'].sourceUrl,
  },
} as const;

export function settlementExpansionSourceQuad(code: string): IslandQuad {
  const quad = naturalShoreGeometry.buildableSourceQuad;
  // Mirror the complete three-quarter island for 5×9 districts, never rotate/shear its cliffs.
  return code === 'garden_quarter' || code === 'market_quarter'
    ? [quad[0], quad[3], quad[2], quad[1]]
    : quad;
}

/** Keep the original central island and its cliff face above every expansion ground layer. */
export const settlementPrimaryPreservationOutline = [
  { x: 526, y: 350 },
  { x: 667, y: 308 },
  { x: 825, y: 304 },
  { x: 1075, y: 349 },
  { x: 1145, y: 390 },
  { x: 1170, y: 459 },
  { x: 1100, y: 536 },
  { x: 968, y: 591 },
  { x: 875, y: 633 },
  { x: 746, y: 657 },
  { x: 594, y: 615 },
  { x: 484, y: 561 },
  { x: 397, y: 479 },
  { x: 398, y: 431 },
  { x: 469, y: 382 },
] as const;

/** Only server-unlocked expansions create land; no invented buildings, rewards or territory. */
export function settlementExpansionGround(
  code: string,
  unlocked: boolean,
  legalTarget: IslandQuad,
) {
  if (!unlocked || !Object.hasOwn(SETTLEMENT_ISLAND_DISTRICTS, code) || code === 'lakeside_outpost')
    return undefined;
  if (!Object.hasOwn(settlementExpansionGroundManifest, code)) return undefined;
  const asset =
    settlementExpansionGroundManifest[code as keyof typeof settlementExpansionGroundManifest];
  const source: IslandQuad = [
    { x: 0, y: 0 },
    { x: asset.width, y: 0 },
    { x: asset.width, y: asset.height },
    { x: 0, y: asset.height },
  ];
  const matrix = islandHomography(settlementExpansionSourceQuad(code), legalTarget);
  if (!matrix) return undefined;
  const projected = source.map((point) => applyIslandHomography(matrix, point));
  if (projected.some((point) => !point)) return undefined;
  return { asset, source, target: projected as unknown as IslandQuad };
}
